/**
 * Vault-level configurator model. Options apply a material to a viewer group
 * (matched by ordinal + LABEL, see `matchGroupTargets` — group ids are three.js
 * material uuids and are regenerated on every load) through the viewer's stable
 * per-group setters — independent of the viewer's internal variation system
 * (which doesn't persist via vjson).
 * Shared by the Studio builder (authoring) and the embed (runtime).
 */

import { kindFromName } from "../../ui/editor/materials/materialMatcher";

export type OptionKind = "metal" | "gem";

/** The material library's five categories — iJewel's tabs. */
export type PresetCategory = "metal" | "gem" | "ceramic" | "pearl" | "other";

/**
 * A material from the studio's library, as an option applies it.
 *
 * Exactly one of `url` / `presetId` says WHAT is applied:
 * - `url`: a `.pmat` / `.dmat` file (or the `data:` URL a workspace upload
 *   travels as). Always ABSOLUTE — a shopper's browser opens the shared link
 *   with no idea which origin a relative path meant. See `resolveAssetUrl`.
 * - `presetId`: one of the viewer's built-ins (`listMaterialPresets` ids, the
 *   named metals and stones).
 */
export interface OptionPreset {
  kind: PresetCategory;
  url?: string;
  presetId?: string;
  /** Named metals only ("polished", "hammered"…). */
  finishId?: string;
  /**
   * "dmat" when the file is a diamond whatever its name says — a `.pmat` can
   * DECLARE a DiamondMaterial, and the viewer applies it as one. Learned the
   * first time the studio applies it; absent means "go by the URL".
   */
  format?: "pmat" | "dmat";
}

export interface ConfiguratorOption {
  id: string;
  name: string;
  swatch: string; // display chip hex
  /** Legacy (colour-only) options: applied via setGroupMetalColor. */
  metalColor?: string;
  /** Legacy (colour-only) options: applied via applyGemPresetToGroup. */
  gemPresetId?: string;
  /** A library material. Wins over the two legacy fields when present. */
  preset?: OptionPreset;
  /** Rendered preview of `preset` (absolute). Without one the swatch is drawn. */
  thumbUrl?: string;
  priceDelta?: number; // added to the base price when selected
}

export interface ConfiguratorOptionGroup {
  id: string;
  label: string; // customer-facing label, e.g. "Metal" / "Center Stone"
  targetGroupLabel: string; // viewer group label, e.g. "Metal 01"
  /** Position of the target group in the model's `listGroups()` order. Material
   *  names repeat inside a real GLB, so the label alone is not a key — without
   *  this the second group of a duplicate-named pair is lost on reload.
   *  Optional: defs authored before it was recorded still match by label. */
  targetGroupIndex?: number;
  kind: OptionKind;
  /** Index into `options` (already filtered to the included ones) shown first. */
  defaultIndex: number;
  options: ConfiguratorOption[];
}

/** A selectable whole-model choice (a different GLB) the customer can switch to.
 *  Variants share the configurator's per-part option groups (matched by label). */
export interface ConfiguratorVariant {
  id: string;
  label: string;
  modelId: string; // vault model id; GLB resolved via vaultApi.getModelGlbUrl
  thumbUrl?: string;
}

export interface ConfiguratorDef {
  groups: ConfiguratorOptionGroup[];
  basePrice?: number; // starting price before option deltas
  currency?: string; // ISO 4217, default USD
  /** Selectable models; index 0 is the base model. Absent/≤1 = single-model. */
  variants?: ConfiguratorVariant[];
}

/** The option a group opens on, clamped — a def can outlive the option list it
 *  was written against (an option removed, or a legacy hard-coded index). */
export function defaultIndexOf(group: ConfiguratorOptionGroup): number {
  const i = Math.trunc(group.defaultIndex);
  if (!Number.isFinite(i) || i < 0) return 0;
  return Math.min(i, Math.max(0, group.options.length - 1));
}

/** Total price for a set of active option indices (one per group). */
export function computePrice(def: ConfiguratorDef, activeIndexByGroupId: Record<string, number>): number {
  let total = def.basePrice ?? 0;
  for (const g of def.groups) {
    const opt = g.options[activeIndexByGroupId[g.id] ?? defaultIndexOf(g)];
    total += opt?.priceDelta ?? 0;
  }
  return total;
}

export function formatPrice(amount: number, currency = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

/** Standard metal finishes for metal groups. */
export const METAL_FINISHES: ConfiguratorOption[] = [
  { id: "wg", name: "White Gold", swatch: "#e9e7e1", metalColor: "#ece9e2" },
  { id: "yg", name: "Yellow Gold", swatch: "#e8c451", metalColor: "#f0cf73" },
  { id: "rg", name: "Rose Gold", swatch: "#dca58c", metalColor: "#e3b49a" },
  { id: "pt", name: "Platinum", swatch: "#dcdcdc", metalColor: "#e5e4e2" },
];

/** Standard gemstone options for gem groups (viewer DIAMOND_PRESETS ids). */
export const GEM_OPTIONS: ConfiguratorOption[] = [
  { id: "diamond", name: "Diamond", swatch: "#eef0f2", gemPresetId: "diamondClear" },
  { id: "ruby", name: "Ruby", swatch: "#9b111e", gemPresetId: "ruby" },
  { id: "sapphire", name: "Sapphire", swatch: "#0f52ba", gemPresetId: "sapphire" },
  { id: "emerald", name: "Emerald", swatch: "#046307", gemPresetId: "emerald" },
];

export function optionsForKind(kind: OptionKind): ConfiguratorOption[] {
  return kind === "gem" ? GEM_OPTIONS : METAL_FINISHES;
}

export function cleanLabel(label: string, kind: OptionKind): string {
  // CAD layer names: "METAL01" → "Metal", "Gem_01" → "Gem", "18K_Rose" → "18K Rose".
  const base = label
    .replace(/[_-]+/g, " ")
    .replace(/([A-Za-z])(\d)/g, "$1 $2")
    .replace(/\b\d+\b/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (/^[A-Z]{4,}$/.test(w) ? w[0] + w.slice(1).toLowerCase() : w))
    .join(" ");
  const fallback = kind === "gem" ? "Center Stone" : "Metal";
  // Generic viewer labels ("Group", "Object", "Mesh") read poorly to a buyer.
  if (!base || /^(group|object|mesh|default|node)$/i.test(base)) return fallback;
  return base;
}

/** Auto-build a configurator from the model's metal + gem groups. `index` is the
 *  group's position in the full `listGroups()` list, when the caller knows it. */
export function autoConfigurator(groups: { label: string; kind: OptionKind; index?: number }[]): ConfiguratorDef {
  const metal = groups.filter((g) => g.kind === "metal").slice(0, 2);
  const gem = groups.filter((g) => g.kind === "gem").slice(0, 1);
  const picked = [...metal, ...gem];
  return {
    groups: picked.map((g, i) => ({
      id: `${g.kind}-${i}`,
      label: cleanLabel(g.label, g.kind),
      targetGroupLabel: g.label,
      targetGroupIndex: g.index,
      kind: g.kind,
      defaultIndex: 0,
      options: optionsForKind(g.kind),
    })),
  };
}

/** A viewer group, as far as configurator matching is concerned. */
export interface GroupTarget {
  label: string;
  kind: string;
}

// ─── A part's kind, kept stable ──────────────────────────────────────────────
//
// What the viewer reports a part to be RIGHT NOW is not what the part is. A
// pearl, a ceramic or any metal file put on the stones makes the viewer file
// them as metal (a PBR surface), and Basic's "Other" panel, a legacy colour
// choice or the "Is a part wrong?" switch can all do it. The configurator used
// to copy that live kind into the saved def at the first pick — so one stray
// metal on "Gem 01" saved the stone slot as metal for good, and its "+" then
// offered only metals.

const asKind = (k: string | null | undefined): OptionKind | null => (k === "gem" || k === "metal" ? k : null);

/**
 * The kind a configurator slot IS, whatever its part happens to be wearing:
 *
 *   1. what the file's own NAME says ("Gem 01", "diamond", "Stone_3" are
 *      stones; "Metal 01", "18K_Rose" metal — `kindFromName`);
 *   2. otherwise the kind the viewer reported when the piece LOADED
 *      (`loadKind`, recorded once per load — `state/editor/modelLoads.ts`);
 *   3. otherwise the kind it reports now.
 */
export function slotKindOf(group: { label: string; kind: string }, loadKind?: string | null): OptionKind {
  return kindFromName(group.label) ?? asKind(loadKind) ?? asKind(group.kind) ?? "metal";
}

/**
 * A saved def with every group's `kind` agreeing with what its part's NAME
 * says. Defs saved while a stone part was showing as metal carry
 * `kind: "metal"` for "Gem 01"; this reads them back as the stones they are.
 * Groups whose name says nothing keep their stored kind. Pure; returns the
 * SAME object when nothing needed correcting.
 */
export function correctConfiguratorKinds<D extends ConfiguratorDef | undefined | null>(def: D): D {
  if (!def?.groups?.length) return def;
  let changed = false;
  const groups = def.groups.map((g) => {
    const named = kindFromName(g.targetGroupLabel);
    if (!named || named === g.kind) return g;
    changed = true;
    return { ...g, kind: named };
  });
  return changed ? ({ ...def, groups } as D) : def;
}

/**
 * Pair saved groups with the live viewer groups they target, in def order.
 *
 * Strongest evidence first: the recorded ordinal, then label + kind, then label
 * alone (defs written before ordinals, or a model whose group order shifted).
 * Each viewer group can be claimed only once — matching on the label alone let
 * two same-named groups resolve to the same target and silently dropped one.
 */
export function matchGroupTargets<G extends ConfiguratorOptionGroup, T extends GroupTarget>(
  groups: G[],
  targets: T[],
): { group: G; target: T; targetIndex: number }[] {
  const claimed = new Set<number>();
  const found = new Map<G, number>();
  const passes: ((g: G, t: T, i: number) => boolean)[] = [
    (g, t, i) => i === g.targetGroupIndex && t.label === g.targetGroupLabel,
    (g, t) => t.label === g.targetGroupLabel && t.kind === g.kind,
    (g, t) => t.label === g.targetGroupLabel,
  ];
  for (const matches of passes) {
    for (const g of groups) {
      if (found.has(g)) continue;
      const i = targets.findIndex((t, ti) => !claimed.has(ti) && matches(g, t, ti));
      if (i < 0) continue;
      claimed.add(i);
      found.set(g, i);
    }
  }
  return groups.flatMap((g) => {
    const i = found.get(g);
    return i == null ? [] : [{ group: g, target: targets[i], targetIndex: i }];
  });
}

/**
 * The def a viewer should actually render: the saved one, or an auto-built set
 * of groups when nothing usable was authored. `??` on the raw field is not
 * enough — the builder used to drop every emptied group, so a saved def can be
 * `{ groups: [] }`, which renders zero swatches instead of falling back.
 */
export function effectiveConfigurator(
  saved: ConfiguratorDef | undefined,
  viewerGroups: GroupTarget[],
): ConfiguratorDef {
  if (saved?.groups?.length) return saved;
  const auto = autoConfigurator(
    viewerGroups
      .map((g, index) => ({ label: g.label, kind: g.kind as OptionKind, index }))
      .filter((g) => g.kind === "metal" || g.kind === "gem"),
  );
  // Keep whatever else the designer did save (price, currency, variants).
  return saved ? { ...saved, groups: auto.groups } : auto;
}

// ─── Editing (the studio bar and the builder share these) ────────────────────
//
// Pure functions over a def: every one returns a NEW def and never mutates its
// input, so a draft can be compared against what was saved by value.

/** A live material slot: one entry of the viewer's `listGroups()`. */
export interface SlotTarget {
  label: string;
  /** The kind the viewer reports NOW. */
  kind: string;
  /** Position in `listGroups()` order. */
  index: number;
  /** The kind the viewer reported when the piece loaded, if recorded. */
  loadKind?: string | null;
}

/** True when a def has anything for a customer to switch: one part with one option. */
export function hasChoices(def: ConfiguratorDef | undefined | null): boolean {
  return !!def?.groups?.some((g) => g.options.length > 0);
}

/**
 * The def group each live slot is configured by, keyed by the slot's index in
 * `slots`. Same pairing the embed uses (`matchGroupTargets`), so the studio
 * edits exactly the group a shopper will see.
 */
export function groupsBySlot(def: ConfiguratorDef, slots: GroupTarget[]): Map<number, ConfiguratorOptionGroup> {
  return new Map(matchGroupTargets(def.groups, slots).map((m) => [m.targetIndex, m.group]));
}

function freshId(taken: Set<string>, base: string): string {
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) if (!taken.has(`${base}-${n}`)) return `${base}-${n}`;
}

/**
 * The def with a group for `slot`, and that group's id. Adds an empty group
 * (labelled for a customer, targeting the slot by label + ordinal) when the
 * slot has none yet.
 *
 * The group's kind is the SLOT's (`slotKindOf`), never a copy of whatever the
 * part is wearing at that moment — and an existing group whose stored kind
 * disagrees is put right on the way, so a def saved during the bug heals on
 * its next edit.
 */
export function withSlotGroup(
  def: ConfiguratorDef,
  slot: SlotTarget,
  slots: GroupTarget[],
): { def: ConfiguratorDef; groupId: string } {
  const kind = slotKindOf(slot, slot.loadKind);
  const existing = groupsBySlot(def, slots).get(slot.index);
  if (existing) {
    if (existing.kind === kind) return { def, groupId: existing.id };
    return { def: mapGroup(def, existing.id, (g) => ({ ...g, kind })), groupId: existing.id };
  }
  const id = freshId(new Set(def.groups.map((g) => g.id)), `${kind}-${slot.index}`);
  const group: ConfiguratorOptionGroup = {
    id,
    label: cleanLabel(slot.label, kind),
    targetGroupLabel: slot.label,
    targetGroupIndex: slot.index,
    kind,
    defaultIndex: 0,
    options: [],
  };
  return { def: { ...def, groups: [...def.groups, group] }, groupId: id };
}

function mapGroup(
  def: ConfiguratorDef,
  groupId: string,
  fn: (g: ConfiguratorOptionGroup) => ConfiguratorOptionGroup,
): ConfiguratorDef {
  return { ...def, groups: def.groups.map((g) => (g.id === groupId ? fn(g) : g)) };
}

/**
 * Add `option` to a group, or find it if that material is already there (the
 * same library item twice is one choice, not two). Returns where it sits.
 */
export function addOption(
  def: ConfiguratorDef,
  groupId: string,
  option: ConfiguratorOption,
): { def: ConfiguratorDef; index: number } {
  const group = def.groups.find((g) => g.id === groupId);
  if (!group) return { def, index: -1 };
  const found = group.options.findIndex((o) => o.id === option.id);
  if (found >= 0) return { def, index: found };
  return {
    def: mapGroup(def, groupId, (g) => ({ ...g, options: [...g.options, option] })),
    index: group.options.length,
  };
}

/**
 * Remove an option. The group keeps opening on the SAME option it opened on
 * before (its index shifts down past the removed one); removing that option
 * itself falls back to the first.
 */
export function removeOption(def: ConfiguratorDef, groupId: string, optionId: string): ConfiguratorDef {
  return mapGroup(def, groupId, (g) => {
    const at = g.options.findIndex((o) => o.id === optionId);
    if (at < 0) return g;
    const opens = defaultIndexOf(g);
    const options = g.options.filter((_, i) => i !== at);
    const defaultIndex = at < opens ? opens - 1 : at === opens ? 0 : opens;
    return { ...g, options, defaultIndex: Math.min(defaultIndex, Math.max(0, options.length - 1)) };
  });
}

/** The option a group opens on — what a shopper sees first. */
export function setDefaultOption(def: ConfiguratorDef, groupId: string, index: number): ConfiguratorDef {
  return mapGroup(def, groupId, (g) => (index >= 0 && index < g.options.length ? { ...g, defaultIndex: index } : g));
}

/** Replace one option's fields (price, a learned file format…). */
export function patchOption(
  def: ConfiguratorDef,
  groupId: string,
  optionId: string,
  patch: Partial<ConfiguratorOption>,
): ConfiguratorDef {
  return mapGroup(def, groupId, (g) => ({
    ...g,
    options: g.options.map((o) => (o.id === optionId ? { ...o, ...patch } : o)),
  }));
}

/** Rename a group as the customer sees it. */
export function setGroupLabel(def: ConfiguratorDef, groupId: string, label: string): ConfiguratorDef {
  return mapGroup(def, groupId, (g) => ({ ...g, label }));
}
